// @flow

import React, {useCallback, useState} from 'react';
import {
  Divider,
  makeStyles,
  Typography,
  CircularProgress,
  MenuItem,
} from '@material-ui/core';

import {Add} from '@material-ui/icons';

import {colors} from '../../../../../assets/styles/colors';

import HorizontalScroll from '../../../../../elements/HorizontalFilter/HorizontalScroll/HorizontalScroll';

import CustomButton from '../../../../../elements/CustomButton/CustomButton';

import ContactInformationSection from './sections/ContactInformation';
import SectionCollapse from './sections/SectionCollapse';
import SellInfo from './sections/SellInfo';
import ScheduleMessageList from './sections/ScheduleMessageList';
import ConversationNotesSection from '../../../notes/components/ConversationNotesSection';
import {useCollaborators} from '../../../../configure/accounts/utils';
import {
  useCurrentConversation,
  useSetTagToConversation,
  useCurrentConversationActions,
  useCurrentConversationState,
} from '../../../chat/utils/conversation';
import AddConversationLabelModal from '../../../chat/components/Modals/AddConversationLabelModal';
import ConversationLabel from '../../../chat/components/MultiChat/ConversationLabel/ConversationLabel';
import TiendadaTooltip from '../../../../../elements/tiendada-tooltip';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    overflow: 'auto',
    minWidth: '300px',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  collapsedContent: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '.5rem',
  },
  content: {
    width: '100%',
    height: '100%',
    overflow: 'hidden auto',
    padding: '0',
  },
  contentEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  innerDivider: {
    border: '1px solid #E8E8ED',
    backgroundColor: 'unset',
    width: '100%',
  },
  title: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 600,
  },
  subtitle: {
    color: colors.grey900,
    fontSize: 14,
    whiteSpace: 'nowrap',
    fontWeight: 500,
  },
  assignedName: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 500,
    padding: '.13rem .5rem',
    borderRadius: '.5rem',
    background: colors.grey200,
  },
  greyText: {
    color: colors.grey700,
    fontSize: 14,
    fontWeight: 400,
  },
  section: {
    padding: theme.spacing(1),
  },
  row: {
    margin: theme.spacing(1, 0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  iconButton: {},
  horizontalScroll: {
    paddingBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  tagConversationLoader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#ffffff80',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shortNameWrap: {
    background: '#E5DCFC',
    height: 56,
    width: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  },
  header: {
    display: 'flex',
    paddingBottom: '1rem',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '.25rem',

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  floatEdit: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: '.5rem',

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  expandInboxButton: {
    padding: '.5rem',
    '&[data-expanded="true"]': {
      transform: 'rotate(180deg)',
    },
  },
  tooltip: {
    background: colors.grey700,
    boxShadow: '0px 4px 12px rgba(14, 21, 58, 0.08)',
    borderRadius: '4px',
    padding: '.5rem .75rem',
    color: 'white',
    fontSize: '.85rem',
    fontWeight: 500,
  },
  arrow: {
    color: colors.grey700,
  },
  profileIconItem: {
    color: colors.grey700,
    fontSize: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '.5rem',
  },
}));

const EMPTY_COLLABORATOR = 'empty';
const ADMIN_OPTION = 'admin';

export default function FunnelContactProfile() {
  const classes = useStyles();

  const {collaborators} = useCollaborators();

  const {currentConversation: conversation} = useCurrentConversation();
  const {setTagToConversation, loading} = useSetTagToConversation(
    conversation ? conversation.id : ''
  );

  const {
    assignCollaborator,
    assignToMe,
    clearAssignment,
  } = useCurrentConversationActions();

  const {state} = useCurrentConversationState();

  const [openLabelModal, setOpenLabelModal] = useState(false);

  const handleCloseLabelModal = useCallback(() => {
    setOpenLabelModal(false);
  }, []);

  if (!conversation) {
    return (
      <div className={classes.root}>
        <div className={classes.contentEmpty}>Seleccione una conversación</div>
      </div>
    );
  }

  const {crmCustomer, tags = [], contactInformation} = conversation;

  const handleRemoveTag = (tagId: string) => {
    const newTags = tags
      .filter(tag => tag.id !== tagId)
      .map(tag => tag.id.replace('CT', 'CTA'));
    setTagToConversation(newTags);
  };

  const getAssigned = () => {
    if (!conversation) return EMPTY_COLLABORATOR;

    const {userId, userType} = conversation;

    const admin = userType === 'VENDOR';
    const collaboratorId =
      userType === 'COLLABORATOR' && userId && `C${userId}`;
    if (admin) return ADMIN_OPTION;
    if (collaboratorId) return collaboratorId;
    return EMPTY_COLLABORATOR;
  };

  const assigned = getAssigned();

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.section}>
          <div className={classes.row} style={{margin: 0}}>
            <Typography className={classes.subtitle}>Asignado a:</Typography>

            <TiendadaTooltip
              title="Las conversaciones cerradas no se pueden asignar"
              disableHoverListener={state !== 'CLOSED'}
            >
              <div>
                <CustomSelect
                  pagination
                  value={assigned}
                  disabled={state === 'CLOSED'}
                  onChange={e => {
                    const value = e.target.value;

                    if (value === EMPTY_COLLABORATOR) {
                      clearAssignment();
                      return;
                    }
                    if (value === ADMIN_OPTION) {
                      assignToMe();
                      return;
                    }
                    assignCollaborator(value);
                  }}
                >
                  <MenuItem value={EMPTY_COLLABORATOR}>Sin asignar</MenuItem>
                  {collaborators.map(collaborator => (
                    <MenuItem key={collaborator.id} value={collaborator.id}>
                      {collaborator.firstName}
                    </MenuItem>
                  ))}
                  <MenuItem value={ADMIN_OPTION}>Administrador</MenuItem>
                </CustomSelect>
              </div>
            </TiendadaTooltip>
          </div>
        </div>
        <Divider className={classes.innerDivider} />
        {(contactInformation || crmCustomer) && (
          <SectionCollapse label="Información de contacto">
            <ContactInformationSection
              contactInformation={contactInformation}
              crmCustomer={crmCustomer}
            />
          </SectionCollapse>
        )}

        <Divider className={classes.innerDivider} />

        <SectionCollapse label="Etiquetas de conversación">
          <HorizontalScroll className={classes.horizontalScroll}>
            {loading && (
              <div className={classes.tagConversationLoader}>
                <CircularProgress size={24} />
              </div>
            )}
            <CustomButton
              variant="text"
              color="primary"
              onClick={() => setOpenLabelModal(true)}
            >
              <Add fontSize="small" />
              Agregar
            </CustomButton>
            {tags?.map(tag => (
              <ConversationLabel
                key={tag.id}
                label={tag.tag}
                disabled={loading}
                onDelete={() => {
                  handleRemoveTag(tag.id);
                }}
              />
            ))}
          </HorizontalScroll>
        </SectionCollapse>

        <Divider className={classes.innerDivider} />

        {!!crmCustomer ? (
          <SectionCollapse label="Datos de compra">
            <SellInfo conversation={conversation} crmCustomer={crmCustomer} />
          </SectionCollapse>
        ) : (
          <div className={classes.section}>
            <Typography className={classes.subtitle}>
              No hay datos de compra
            </Typography>
          </div>
        )}

        <SectionCollapse label="Mensajes Programados">
          <ScheduleMessageList />
        </SectionCollapse>

        <Divider className={classes.innerDivider} />

        <SectionCollapse label="Notas">
          <ConversationNotesSection conversationId={conversation.id} />
        </SectionCollapse>
      </div>

      {openLabelModal && (
        <AddConversationLabelModal
          open={openLabelModal}
          //$FlowFixMe
          tags={tags}
          onSave={params => {
            console.log({
              params,
            });
            setTagToConversation(params.map(tag => tag.id));
          }}
          handleClose={handleCloseLabelModal}
        />
      )}
    </div>
  );
}
